import React, { useState } from 'react';

import { TextField, useTheme } from '@mui/material';
import type { GridFilterInputValueProps } from '@mui/x-data-grid-premium';
import clsx from 'clsx';
import type { ContractTypeOption } from 'src/components';
import { AlineCheckbox, ContractTypeAutocomplete, FilterTags } from 'src/components';
import { makeStyles } from 'tss-react/mui';

import {
  filterAutocompleteStyle,
  usePreserveAutocompleteSearchInput,
} from './autocomplete-filter-utils';

const useStyles = makeStyles()(() => ({
  autocomplete: filterAutocompleteStyle,
}));

export function ContractTypeAutocompleteFilter({
  applyValue,
  item,
}: GridFilterInputValueProps): JSX.Element {
  const theme = useTheme();
  const { classes } = useStyles();
  const [truncateTags, setTruncateTags] = useState(true);
  const [value, setValue] = useState<ContractTypeOption[]>(item.value ?? []);
  const { inputValue, onInputChange } = usePreserveAutocompleteSearchInput();

  function handleValueChange(value: ContractTypeOption[]): void {
    applyValue({
      ...item,
      value,
    });
    setValue(value);
  }

  return (
    <ContractTypeAutocomplete
      disableCloseOnSelect
      multiple
      blurOnSelect={false}
      className={classes.autocomplete}
      inputValue={inputValue}
      limitTags={2}
      renderInput={params => {
        return <TextField placeholder="Search contract types..." {...params} variant="standard" />;
      }}
      renderOption={(props, option, state) => {
        const { className, key, ...otherProps } = props;
        return (
          <li key={key} {...otherProps} className={clsx(className)}>
            <AlineCheckbox checked={state.selected} style={{ marginRight: theme.spacing(1) }} />
            {option.value}
          </li>
        );
      }}
      renderValue={contractTypes => {
        return (
          <FilterTags<ContractTypeOption>
            getTextFromTag={tag => tag.value}
            tags={contractTypes}
            truncateTags={truncateTags}
            onDelete={contractType =>
              handleValueChange(
                contractTypes.filter(
                  type =>
                    contractType.__typename === 'ContractType' &&
                    type.__typename === 'ContractType' &&
                    contractType.id !== type.id
                )
              )
            }
          />
        );
      }}
      slotProps={{
        popper: {
          modifiers: [
            {
              enabled: false,
              name: 'flip',
            },
            {
              enabled: false,
              name: 'preventOverflow',
            },
          ],
        },
      }}
      suggestNewOption={false}
      value={value}
      onBlur={e => {
        e.stopPropagation();
        setTruncateTags(true);
      }}
      onChange={(_e, value) => handleValueChange(value)}
      onFocus={e => {
        e.stopPropagation();
        setTruncateTags(false);
      }}
      onInputChange={onInputChange}
    />
  );
}
